import { api } from "jiffy-api";

export const PRICING_REQUEST_SOURCE = "buy_it_again";
export const QTY_DEBOUNCE_MS = 150;

const formatPrice = value => `$${Number(value).toFixed(2)}`;

const isUsableArtwork = artwork =>
  artwork != null &&
  artwork.unit_price != null &&
  artwork.unit_price_total != null &&
  Number(artwork.unit_price_total) > 0;

export default class BuyItAgainDtfPriceController {
  #getModal;

  #getQty;

  #params = null;

  #abort = null;

  #debounceTimer = null;

  #hasInitialPrice = false;

  constructor({ getModal, getQty }) {
    this.#getModal = getModal;
    this.#getQty = getQty;
  }

  configure(params) {
    // Abort any in-flight fetch before swapping params so a late response from
    // the previous line item cannot land against the new params (defensive —
    // the post-await `signal.aborted` check also guards this, but aborting at
    // the boundary is clearer).
    this.abort();
    this.#params = params;
    this.#hasInitialPrice = false;
    this.#resetUI();
  }

  scheduleFetch() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = null;
      this.fetch();
    }, QTY_DEBOUNCE_MS);
  }

  // Update dims without resetting #hasInitialPrice so the next fetch stays in
  // soft-loading mode (no shimmer, submit stays clickable). configure() can't
  // be reused here because it wipes initial-price state.
  updateDimensions(width, height) {
    if (!this.#params) return;
    this.#params = { ...this.#params, widthValue: width, heightValue: height };
  }

  async fetch() {
    if (!this.#params?.dtfPrintId || !this.#params?.lineItemId) return;

    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;

    const isFirstFetch = !this.#hasInitialPrice;
    this.#setLoadingState({ isFirstFetch, isUpdating: !isFirstFetch });

    try {
      const response = await api.dtf.dtfPricing(
        { product: this.#params.lineItemId },
        this.#buildBody(),
        { useAxiosForRequest: true, signal: controller.signal }
      );
      if (controller.signal.aborted) return;

      const artwork = response?.artworks?.[this.#params.dtfPrintId];
      if (!isUsableArtwork(artwork)) {
        this.#renderFallback();
        return;
      }
      this.#renderPrice(artwork);
      this.#hasInitialPrice = true;
    } catch (error) {
      if (error?.name === "AbortError" || controller.signal.aborted) return;
      this.#renderFallback();
    } finally {
      if (this.#abort === controller) {
        this.#abort = null;
        this.#setLoadingState({ isFirstFetch: false, isUpdating: false });
      }
    }
  }

  abort() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
    this.#abort?.abort();
    this.#abort = null;
  }

  #buildBody() {
    return {
      artworks: [
        {
          artwork_id: this.#params.dtfPrintId,
          width: this.#params.widthValue,
          height: this.#params.heightValue,
          quantity: this.#getQty(),
          enhancement_tier: this.#params.enhancementTier
        }
      ],
      pre_cut: this.#params.preCut,
      request_source: PRICING_REQUEST_SOURCE
    };
  }

  #renderPrice({ unit_price: unitPrice, unit_price_total: unitPriceTotal, percentage }) {
    const $modal = this.#getModal();
    if (!$modal) return;
    const $unit = $modal.querySelector(".js-unit-price");
    const $total = $modal.querySelector(".js-total-price");
    const $fallback = $modal.querySelector(".js-price-fallback");
    const $savings = $modal.querySelector(".js-price-savings");
    if ($unit) $unit.textContent = formatPrice(unitPrice);
    if ($total) $total.textContent = formatPrice(unitPriceTotal);
    if ($fallback) $fallback.classList.add("hidden");
    if ($savings) {
      const pct = Number(percentage);
      if (Number.isFinite(pct) && pct > 0) {
        $savings.textContent = `save ${pct.toFixed(0)}%`;
        $savings.classList.remove("hidden");
      } else {
        $savings.textContent = "";
        $savings.classList.add("hidden");
      }
    }
    $modal.querySelectorAll(".buy-it-again-dtf-modal__price-row").forEach($r => {
      $r.classList.remove("hidden");
    });
  }

  #renderFallback() {
    const $modal = this.#getModal();
    if (!$modal) return;
    const $fallback = $modal.querySelector(".js-price-fallback");
    const $savings = $modal.querySelector(".js-price-savings");
    if ($fallback) $fallback.classList.remove("hidden");
    if ($savings) {
      $savings.textContent = "";
      $savings.classList.add("hidden");
    }
    $modal.querySelectorAll(".buy-it-again-dtf-modal__price-row").forEach($r => {
      $r.classList.add("hidden");
    });
    // Treat fallback as a settled state so the submit stays clickable on
    // subsequent qty changes. addToCartPrint is authoritative anyway.
    this.#hasInitialPrice = true;
  }

  #setLoadingState({ isFirstFetch, isUpdating }) {
    const $modal = this.#getModal();
    if (!$modal) return;
    const $wrapper = $modal.querySelector(".js-price-wrapper");
    const $submit = $modal.querySelector(".js-submit");
    // Same skeleton shimmer for every in-flight fetch — first or refetch.
    // Keeps the visual feedback consistent and removes the dual "shimmer vs.
    // 'Updating...' text" path the modal used to have.
    if ($wrapper) $wrapper.classList.toggle("is-loading", isFirstFetch || isUpdating);
    if ($submit) {
      if (isFirstFetch) $submit.setAttribute("disabled", "disabled");
      else $submit.removeAttribute("disabled");
    }
  }

  #resetUI() {
    const $modal = this.#getModal();
    if (!$modal) return;
    const $unit = $modal.querySelector(".js-unit-price");
    const $total = $modal.querySelector(".js-total-price");
    const $fallback = $modal.querySelector(".js-price-fallback");
    const $wrapper = $modal.querySelector(".js-price-wrapper");
    const $submit = $modal.querySelector(".js-submit");
    const $savings = $modal.querySelector(".js-price-savings");
    if ($unit) $unit.textContent = "—";
    if ($total) $total.textContent = "—";
    if ($fallback) $fallback.classList.add("hidden");
    if ($savings) {
      $savings.textContent = "";
      $savings.classList.add("hidden");
    }
    // Clear lingering loading state so an aborted in-flight fetch followed by a
    // configure() that early-returns (e.g. dtfPrintId = 0) does not leave the
    // submit button permanently disabled or the shimmer running.
    if ($wrapper) $wrapper.classList.remove("is-loading");
    if ($submit) $submit.removeAttribute("disabled");
    $modal.querySelectorAll(".buy-it-again-dtf-modal__price-row").forEach($r => {
      $r.classList.remove("hidden");
    });
  }
}
